
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '一般咨询',
    message: '',
    priority: '普通'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { configInfo } = useSelector((state: RootState) => state.settings);
  const contactMethods = [
    {
      title: '客服热线',
      description: '7×15小时专业客服为您服务',
      icon: 'ri-phone-line',
      info: configInfo?.contact_info || '400-888-8888',
      subInfo: '服务时间：9:00-24:00',
      color: 'green',
      action: 'tel:400-888-8888'
    },
    {
      title: '在线客服',
      description: '24小时智能客服，实时解答问题',
      icon: 'ri-customer-service-2-line',
      info: '点击开始对话',
      subInfo: '平均响应时间 < 30秒',
      color: 'blue',
      action: '#'
    },
    {
      title: '邮件支持',
      description: '详细问题描述，专业团队回复',
      icon: 'ri-mail-line',
      info: configInfo?.service_email ||'service@bstyx.com',
      subInfo: '24小时内回复',
      color: 'purple',
      action: 'mailto:service@bstyx.com'
    },
    {
      title: '商务合作',
      description: '版权合作、广告投放等商务洽谈',
      icon: 'ri-handshake-line',
      info: configInfo?.business_email ||'business@bstyx.com',
      subInfo: '工作日48小时内回复',
      color: 'orange',
      action: 'mailto:business@bstyx.com'
    }
  ];

  const departments = [
    {
      name: '技术支持',
      description: '播放问题、账户异常、功能故障',
      icon: 'ri-tools-line',
      email: 'tech@bstyx.com',
      phone: '400-888-8888 转 1'
    },
    {
      name: '会员服务',
      description: 'VIP开通、续费、退款相关问题',
      icon: 'ri-vip-crown-line',
      email: 'vip@bstyx.com',
      phone: '400-888-8888 转 2'
    },
    {
      name: '内容投诉',
      description: '内容举报、版权问题、违规投诉',
      icon: 'ri-alarm-warning-line',
      email: 'report@bstyx.com',
      phone: '400-888-8888 转 3'
    },
    {
      name: '市场合作',
      description: '广告投放、赞助合作、媒体对接',
      icon: 'ri-marketing-line',
      email: 'marketing@bstyx.com',
      phone: '400-888-8888 转 4'
    }
  ];

  const officeInfo = {
    address: '北京市朝阳区体育大厦15层',
    postcode: '100020',
    workTime: '周一至周五 9:00-18:00',
    coordinates: '116.4074, 39.9042'
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // 模拟提交
    setTimeout(() => {
      alert('消息发送成功！我们会在24小时内回复您。');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '一般咨询',
        message: '',
        priority: '普通'
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-green-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              联系我们
            </h1>
            <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
              我们随时准备为您提供帮助和支持，选择最适合的方式联系我们
            </p>
            <div className="flex items-center justify-center space-x-6 text-blue-100">
              <div className="flex items-center">
                <i className="ri-time-line mr-2"></i>
                <span className="text-sm">响应时间 &lt; 24小时</span>
              </div>
              <div className="flex items-center">
                <i className="ri-customer-service-line mr-2"></i>
                <span className="text-sm">专业团队服务</span>
              </div>
            </div>
          </div>
        </section>

        {/* 联系方式 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">多种联系方式</h2>
              <p className="text-gray-600">选择最便捷的方式与我们取得联系</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {contactMethods.map((method, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm border hover:shadow-md transition-shadow">
                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-4 ${
                    method.color === 'green' ? 'bg-green-100' :
                    method.color === 'blue' ? 'bg-blue-100' :
                    method.color === 'purple' ? 'bg-purple-100' :
                    'bg-orange-100'
                  }`}>
                    <i className={`${method.icon} text-2xl ${
                      method.color === 'green' ? 'text-green-600' :
                      method.color === 'blue' ? 'text-blue-600' :
                      method.color === 'purple' ? 'text-purple-600' :
                      'text-orange-600'
                    }`}></i>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 text-center">
                    {method.description}
                  </p>
                  
                  <div className="text-center">
                    <div className={`font-medium mb-1 ${
                      method.color === 'green' ? 'text-green-600' :
                      method.color === 'blue' ? 'text-blue-600' :
                      method.color === 'purple' ? 'text-purple-600' :
                      'text-orange-600'
                    }`}>
                      {method.info}
                    </div>
                    <div className="text-gray-500 text-xs mb-4">
                      {method.subInfo}
                    </div>
                    
                    <a
                      href={method.action}
                      onClick={(e) => {
                        
                          e.preventDefault();
                          if (method.title == '在线客服') {
                            if (configInfo?.live_url) {
                              window.open(configInfo.live_url, '_blank', 'noopener,noreferrer');
                            }
                          }
                          
                        
                      }}
                      className={`inline-block px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                        method.color === 'green' ? 'bg-green-500 hover:bg-green-600 text-white' :
                        method.color === 'blue' ? 'bg-blue-500 hover:bg-blue-600 text-white' :
                        method.color === 'purple' ? 'bg-purple-500 hover:bg-purple-600 text-white' :
                        'bg-orange-500 hover:bg-orange-600 text-white'
                      }`}
                    >
                      立即联系
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* 部门联系方式 */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">专业部门直通</h3>
                <p className="text-gray-600">根据问题类型，直接联系相关专业部门</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {departments.map((dept, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className={`${dept.icon} text-blue-600 text-xl`}></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">{dept.name}</h4>
                    <p className="text-gray-600 text-sm mb-3">{dept.description}</p>
                    <div className="space-y-1 text-xs text-gray-500">
                      <div>{dept.email}</div>
                      <div>{dept.phone}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 在线留言 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">在线留言</h2>
                <p className="text-gray-600">填写表单发送消息，我们会尽快与您联系</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        手机号
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="请输入您的手机号"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        问题类型
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pr-8"
                      >
                        <option value="一般咨询">一般咨询</option>
                        <option value="技术支持">技术支持</option>
                        <option value="账户问题">账户问题</option>
                        <option value="付费服务">付费服务</option>
                        <option value="内容投诉">内容投诉</option>
                        <option value="商务合作">商务合作</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      优先级
                    </label>
                    <div className="flex space-x-4">
                      {['普通', '紧急', '非常紧急'].map((priority) => (
                        <label key={priority} className="flex items-center">
                          <input
                            type="radio"
                            name="priority"
                            value={priority}
                            checked={formData.priority === priority}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 mr-2"
                          />
                          <span className={`text-sm ${
                            priority === '非常紧急' ? 'text-red-600' :
                            priority === '紧急' ? 'text-orange-600' :
                            'text-gray-700'
                          }`}>
                            {priority}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      详细描述 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                      placeholder="请详细描述您的问题或需求，我们会根据您的描述提供更精准的帮助..."
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>

                  {/* 提交按钮 */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <div className="text-sm text-gray-500">
                      <i className="ri-information-line mr-1"></i>
                      我们承诺24小时内回复您的消息
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          发送中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          发送消息
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* 办公地址 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">办公地址</h2>
                <p className="text-gray-600">欢迎到访我们的办公室进行面对面交流</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* 地址信息 */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      贝斯特体育总部
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <i className="ri-map-pin-line text-blue-600 mt-1"></i>
                        <div>
                          <div className="font-medium text-gray-900">地址</div>
                          <div className="text-gray-600">{officeInfo.address}</div>
                          <div className="text-gray-500 text-sm">邮编：{officeInfo.postcode}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <i className="ri-time-line text-green-600 mt-1"></i>
                        <div>
                          <div className="font-medium text-gray-900">办公时间</div>
                          <div className="text-gray-600">{officeInfo.workTime}</div>
                          <div className="text-gray-500 text-sm">节假日休息</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <i className="ri-subway-line text-purple-600 mt-1"></i>
                        <div>
                          <div className="font-medium text-gray-900">交通指南</div>
                          <div className="text-gray-600">地铁10号线团结湖站A口</div>
                          <div className="text-gray-500 text-sm">步行5分钟</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 访问预约 */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <i className="ri-calendar-check-line text-blue-600 mr-2"></i>
                      访问预约
                    </h4>
                    <p className="text-gray-600 text-sm mb-4">
                      为了更好地为您服务，建议您提前预约访问时间
                    </p>
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      预约访问
                    </button>
                  </div>
                </div>

                {/* 地图 */}
                <div className="relative">
                  <div className="bg-gray-200 rounded-lg overflow-hidden" style={{height: '400px'}}>
                    <iframe
                      src="https://maps.google.com/maps?q=北京市朝阳区体育大厦&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="100%"
                      style={{border: 0}}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="贝斯特体育办公地址"
                    ></iframe>
                  </div>
                  
                  {/* 地图覆盖信息 */}
                  <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-900">贝斯特体育</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 常见问题快速入口 */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              还有其他问题？
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/help"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <i className="ri-question-line mr-2"></i>
                查看帮助中心
              </a>
              <a
                href="/community"
                className="inline-flex items-center px-6 py-3 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-600 hover:text-white transition-colors"
              >
                <i className="ri-discuss-line mr-2"></i>
                社区求助
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
